import type { AppRouter } from "@sunfish/api";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import superjson from "superjson";
import { getDeviceMeta, getDeviceToken } from "~/lib/device-session";
import { getOutbox } from "~/lib/offline/store";
import { runSync } from "~/lib/offline/sync";

const API_URL =
	typeof window !== "undefined"
		? (window as any).ENV?.API_URL || "http://localhost:4000"
		: process.env.API_URL || "http://localhost:4000";

const SYNC_INTERVAL_MS = 20_000;

interface SyncState {
	online: boolean;
	syncing: boolean;
	lastSyncedAt: number | null;
	pendingCount: number;
	syncNow: () => void;
}

const SyncContext = createContext<SyncState | null>(null);

function createBearerClient() {
	return createTRPCClient<AppRouter>({
		links: [
			httpBatchLink({
				url: API_URL,
				transformer: superjson,
				headers() {
					const token = getDeviceToken();
					return token ? { Authorization: `Bearer ${token}` } : {};
				},
			}),
		],
	});
}

export function SyncProvider({ children }: { children: React.ReactNode }) {
	const eventId = useMemo(() => getDeviceMeta()?.eventId ?? null, []);
	const client = useMemo(createBearerClient, []);
	const inFlight = useRef(false);

	const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
	const [syncing, setSyncing] = useState(false);
	const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
	const [pendingCount, setPendingCount] = useState(0);

	const refreshPending = useCallback(async () => {
		if (!eventId) return;
		setPendingCount((await getOutbox(eventId)).length);
	}, [eventId]);

	const syncNow = useCallback(async () => {
		if (!eventId || inFlight.current) return;
		inFlight.current = true;
		setSyncing(true);
		try {
			await runSync(client, eventId);
			setLastSyncedAt(Date.now());
			setOnline(true);
		} catch {
			setOnline(false);
		} finally {
			inFlight.current = false;
			setSyncing(false);
			await refreshPending();
		}
	}, [client, eventId, refreshPending]);

	useEffect(() => {
		if (!eventId) return;
		void syncNow();
		const interval = window.setInterval(() => void syncNow(), SYNC_INTERVAL_MS);
		const handleOnline = () => {
			setOnline(true);
			void syncNow();
		};
		const handleOffline = () => setOnline(false);
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		void refreshPending();
		return () => {
			window.clearInterval(interval);
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, [eventId, syncNow, refreshPending]);

	const value = useMemo<SyncState>(
		() => ({ online, syncing, lastSyncedAt, pendingCount, syncNow }),
		[online, syncing, lastSyncedAt, pendingCount, syncNow],
	);

	return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export function useSync(): SyncState {
	const ctx = useContext(SyncContext);
	if (!ctx) throw new Error("useSync must be used within a SyncProvider");
	return ctx;
}
